import { useTranslation } from 'next-i18next/pages'
import Hero from 'sections/hero/Hero'
import { BackgroundColors } from 'types/layout'
import { Heading, Icon, Img } from '@boltenergy-be/design-system'
import styles from './BrusselsAirport.module.scss'
import classNames from 'classnames'
import StartSimulationCard from 'components/start-simulation-card/StartSimulationCard'
import { BrusselsAirportProps } from 'features/brussels-airport/types'
import BecomeProducer from 'sections/become-producer/BecomeProducer'
import { ClientType } from 'components/start-simulation-card/types'
import parse from 'html-react-parser'
import { isSoldOutWithCapacity } from 'utils/producers'
import { InputGroupKeys, SimulationFieldKeys } from 'store/customer/enums'
import useSelectInputGroup from 'hooks/useSelectInputGroup'
import Link from 'components/link/Link'
import FaqWithDefaultDescription from 'components/faq-with-default-description/FaqWithDefaultDescription'
import { getTotalElectricityVolumeForMeterType } from 'utils/usage'

const BrusselsAirport = ({ brusselsAirportProducer, categories }: BrusselsAirportProps) => {
  // Redux
  const simulation = useSelectInputGroup(InputGroupKeys.SIMULATION)

  // i18n
  const { t } = useTranslation(['brusselsAirport', 'common'])

  // Constants
  const isSoldOut =
    brusselsAirportProducer &&
    isSoldOutWithCapacity(
      brusselsAirportProducer,
      getTotalElectricityVolumeForMeterType(simulation[SimulationFieldKeys.METER_TYPE], simulation[SimulationFieldKeys.USAGE])
    )

  return (
    <main className={styles['brussels-airport']}>
      <Hero
        backgroundColor={BackgroundColors.YELLOW}
        image={{ alt: 'Christel & Rutger - Brussels Airport', publicId: 'website/brussels-airport/hero-ba' }}
      >
        <Hero.Title value={t('hero.title')} />
        <Hero.Paragraph value={t('hero.description')} />

        <Hero.CtaGroup>
          <Link as="a" href="#simulation" variant="layered" representation="button">
            {t('common:calculateProposal')}
          </Link>
        </Hero.CtaGroup>
      </Hero>

      <section className={classNames('section container', styles.section, styles.platform)}>
        <div className="columns">
          <div className={classNames('column is-half', styles['v-centered-description'])}>
            <Heading as="h2" serif>
              {t('platform.title')}
            </Heading>
            <p>{t('platform.description')}</p>
          </div>
          <div className="column is-half">
            <div className={styles.bullets}>
              <div className={styles.bullet}>
                <Icon name="euro" />
                <p>{t('platform.bullets.one')}</p>
              </div>
              <div className={styles.bullet}>
                <Icon name="electricity" />
                <p>{t('platform.bullets.two')}</p>
              </div>
              <div className={styles.bullet}>
                <Icon name="peopleHeart" />
                <p>{t('platform.bullets.three')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className={classNames('section', styles.section, styles.how)} id="simulation">
        <div className="container">
          <Heading as="h2" serif>
            {t(!isSoldOut ? 'how.title' : 'how.soldOut.title')}
          </Heading>

          {!isSoldOut ? (
            <div className={styles.steps}>
              <div className={styles.step}>
                <div>
                  <span>1</span>
                </div>
                <p>{t('how.steps.one')}</p>
              </div>
              <div className={styles.step}>
                <div>
                  <span>2</span>
                </div>
                <p>{t('how.steps.two')}</p>
              </div>
              <div className={styles.step}>
                <div>
                  <span>3</span>
                </div>
                <p>{t('how.steps.three')}</p>
              </div>
            </div>
          ) : (
            <p className={styles['sold-out']}>{t('how.soldOut.description')}</p>
          )}

          <StartSimulationCard fullWidth {...(!isSoldOut && { allowedClientTypes: [ClientType.RESIDENTIAL] })} />
        </div>
      </section>

      <FaqWithDefaultDescription categories={categories} title={t('faq.title')} />

      <section className={classNames('section container', styles.section, styles.why)}>
        <div className="columns">
          <div className="column is-half">
            <Img publicId="website/brussels-airport/why-section-house" fill={false} alt="" />
          </div>
          <div className={classNames('column is-half', styles['v-centered-description'])}>
            <Heading as="h2" variant="d3">
              {t('why.title')}
            </Heading>
            <p>{parse(t('why.description'))}</p>
          </div>
        </div>
      </section>

      <BecomeProducer />

      <section className={classNames('section container', styles.section, styles['stargate-section'])}>
        <div className={styles.stargate}>
          <p>{t('stargate.description')}</p>
          <div>
            <Img publicId="website/brussels-airport/eu" fill={false} alt="" />
            <Img publicId="website/brussels-airport/stargate" fill={false} alt="" />
          </div>
        </div>
      </section>
    </main>
  )
}

export default BrusselsAirport
